import React, { useState, useEffect } from 'react';

const SecureCredentials = () => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const crmAuth = localStorage.getItem('crmAuth') || btoa('admin:kioo2025!');

  const categories = [
    { id: 'website', name: 'Website / Portal', icon: '🌐' },
    { id: 'social', name: 'Social Media', icon: '📱' },
    { id: 'email', name: 'Email Account', icon: '📧' },
    { id: 'banking', name: 'Banking / Finance', icon: '🏦' },
    { id: 'streaming', name: 'Streaming / Broadcasting', icon: '📻' },
    { id: 'cloud', name: 'Cloud Services', icon: '☁️' },
    { id: 'software', name: 'Software / Tools', icon: '🔧' },
    { id: 'api', name: 'API / Integration', icon: '🔗' },
    { id: 'other', name: 'Other', icon: '📁' }
  ];

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/secure-credentials`, {
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCredentials(data.credentials || []);
      }
    } catch (error) {
      console.error('Error fetching credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredential = async (credentialData) => {
    try {
      const url = selectedCredential 
        ? `${BACKEND_URL}/api/crm/secure-credentials/${selectedCredential.id}`
        : `${BACKEND_URL}/api/crm/secure-credentials`;
      
      const response = await fetch(url, {
        method: selectedCredential ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Basic ${crmAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentialData)
      });
      
      if (response.ok) {
        fetchCredentials();
        setShowAddModal(false);
        setShowEditModal(false);
        setSelectedCredential(null);
      } else {
        alert('Error saving credential');
      }
    } catch (error) {
      console.error('Error saving credential:', error);
      alert('Error saving credential');
    }
  };

  const handleDeleteCredential = async (id) => {
    if (!window.confirm('Are you sure you want to delete this credential? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/secure-credentials/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${crmAuth}` }
      });
      
      if (response.ok) {
        fetchCredentials();
      } else {
        alert('Error deleting credential');
      }
    } catch (error) {
      console.error('Error deleting credential:', error);
    }
  };

  const togglePasswordVisibility = (id) => {
    setRevealedPasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      alert(`${label} copied to clipboard!`);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || categories[categories.length - 1];
  };

  const filteredCredentials = credentials.filter(cred => {
    const matchesSearch = 
      cred.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cred.url?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cred.username?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = filterCategory === 'all' || cred.category === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">🔐</div>
          <p className="text-gray-500">Loading secure credentials...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">🔐 Secure Credentials Manager</h1>
        <p className="text-gray-600 mt-1">Safely store and manage usernames, passwords, and access links</p>
      </div>

      {/* Actions Bar */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex gap-3 flex-1 w-full md:w-auto">
            {/* Search */}
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search credentials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
            </div>
            
            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
              ))}
            </select>
          </div>
          
          <button
            onClick={() => {
              setSelectedCredential(null);
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            ➕ Add Credential
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-gray-500 text-sm">Total Stored</p>
          <p className="text-2xl font-bold text-gray-900">{credentials.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-gray-500 text-sm">Categories Used</p>
          <p className="text-2xl font-bold text-gray-900">
            {new Set(credentials.map(c => c.category)).size}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-gray-500 text-sm">With URLs</p>
          <p className="text-2xl font-bold text-gray-900">
            {credentials.filter(c => c.url).length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <p className="text-gray-500 text-sm">Recently Added</p>
          <p className="text-2xl font-bold text-gray-900">
            {credentials.filter(c => {
              const added = new Date(c.created_at);
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return added > weekAgo;
            }).length}
          </p>
        </div>
      </div>

      {/* Credentials Grid */}
      {filteredCredentials.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <span className="text-5xl mb-4 block">🔐</span>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {searchTerm || filterCategory !== 'all' ? 'No matching credentials' : 'No credentials yet'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterCategory !== 'all' 
              ? 'Try adjusting your search or filter' 
              : 'Start by adding your first secure credential'}
          </p>
          {!searchTerm && filterCategory === 'all' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              ➕ Add First Credential
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCredentials.map(credential => {
            const category = getCategoryInfo(credential.category);
            const isRevealed = revealedPasswords[credential.id];
            
            return (
              <div 
                key={credential.id}
                className="bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{category.icon}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900">{credential.name}</h3>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {category.name}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        setSelectedCredential(credential);
                        setShowEditModal(true);
                      }}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteCredential(credential.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                {/* URL */}
                {credential.url && (
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 uppercase tracking-wide">URL</label>
                    <div className="flex items-center gap-2">
                      <a 
                        href={credential.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm truncate flex-1"
                      >
                        {credential.url}
                      </a>
                      <button
                        onClick={() => copyToClipboard(credential.url, 'URL')}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Copy URL"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Username */}
                {credential.username && (
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 uppercase tracking-wide">Username</label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900 flex-1 font-mono">{credential.username}</span>
                      <button
                        onClick={() => copyToClipboard(credential.username, 'Username')}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Copy username"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Password */}
                {credential.password && (
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 uppercase tracking-wide">Password</label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900 flex-1 font-mono">
                        {isRevealed ? credential.password : '••••••••••'}
                      </span>
                      <button
                        onClick={() => togglePasswordVisibility(credential.id)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title={isRevealed ? 'Hide password' : 'Show password'}
                      >
                        {isRevealed ? '🙈' : '👁️'}
                      </button>
                      <button
                        onClick={() => copyToClipboard(credential.password, 'Password')}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Copy password"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Notes */}
                {credential.notes && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <label className="text-xs text-gray-500 uppercase tracking-wide">Notes</label>
                    <p className="text-sm text-gray-600 mt-1">{credential.notes}</p>
                  </div>
                )}
                
                {/* Footer */}
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                  <span>Added {new Date(credential.created_at).toLocaleDateString()}</span>
                  {credential.updated_at !== credential.created_at && (
                    <span>Updated {new Date(credential.updated_at).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <CredentialModal
          credential={selectedCredential}
          categories={categories}
          onSave={handleSaveCredential}
          onClose={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            setSelectedCredential(null);
          }}
        />
      )}
    </div>
  );
};

// Credential Add/Edit Modal
const CredentialModal = ({ credential, categories, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: credential?.name || '',
    category: credential?.category || 'website',
    url: credential?.url || '',
    username: credential?.username || '',
    password: credential?.password || '',
    notes: credential?.notes || ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter a name for this credential');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {credential ? '✏️ Edit Credential' : '➕ Add New Credential'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Facebook Admin, Bank Portal"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
              ))}
            </select>
          </div>
          
          {/* URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL / Link</label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder="https://example.com/login"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username / Email</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              placeholder="admin@example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional details, recovery email, security questions, etc."
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              ⚠️ <strong>Security Note:</strong> Credentials are stored securely. Only authorized CRM users can view them.
            </p>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              {credential ? 'Save Changes' : 'Add Credential'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SecureCredentials;
